/**
 * useVariantSelection
 *
 * Generic selection state for items with shift-click range selection.
 * Adapted from useOrderSelection but works with any `{ id: string }[]`.
 *
 * `setSelected(ids, selected)` sets many ids at once (one state update), for
 * press-and-drag painting (`hooks/useDragSelect.ts`); it leaves the shift
 * anchor alone, so shift-click keeps working from the last clicked row.
 */

import { useState, useCallback, useRef, useEffect } from 'react';

interface UseVariantSelectionOptions {
  items: Array<{ id: string }>;
}

interface UseVariantSelectionReturn {
  selectedIds: Set<string>;
  toggleSelect: (id: string, event?: React.MouseEvent | React.KeyboardEvent) => void;
  selectAll: () => void;
  clearSelection: () => void;
  /** Select (or clear) every listed id that is in `items`, in one update. */
  setSelected: (ids: readonly string[], selected: boolean) => void;
  isAllSelected: boolean;
  isSomeSelected: boolean;
  selectedCount: number;
}

export function useVariantSelection({ items }: UseVariantSelectionOptions): UseVariantSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastSelectedIndexRef = useRef<number | null>(null);
  const previousItemsRef = useRef<Array<{ id: string }>>(items);

  // Reset lastSelectedIndex when items array changes (filters applied)
  // and clean up stale selections
  useEffect(() => {
    if (previousItemsRef.current !== items) {
      lastSelectedIndexRef.current = null;
      previousItemsRef.current = items;

      setSelectedIds((prev) => {
        const itemIds = new Set(items.map((i) => i.id));
        const validIds = new Set(Array.from(prev).filter((id) => itemIds.has(id)));
        if (validIds.size !== prev.size) {
          return validIds;
        }
        return prev;
      });
    }
  }, [items]);

  const toggleSelect = useCallback(
    (id: string, event?: React.MouseEvent | React.KeyboardEvent) => {
      const currentIndex = items.findIndex((i) => i.id === id);
      if (currentIndex === -1) return;

      const isShiftKey = event && 'shiftKey' in event && event.shiftKey;

      if (isShiftKey && lastSelectedIndexRef.current !== null) {
        const start = Math.min(lastSelectedIndexRef.current, currentIndex);
        const end = Math.max(lastSelectedIndexRef.current, currentIndex);
        const rangeIds = items.slice(start, end + 1).map((i) => i.id);

        setSelectedIds((prev) => {
          const next = new Set(prev);
          rangeIds.forEach((rid) => next.add(rid));
          return next;
        });
      } else {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          return next;
        });
      }

      lastSelectedIndexRef.current = currentIndex;
    },
    [items],
  );

  const selectAll = useCallback(() => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
      lastSelectedIndexRef.current = null;
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
      lastSelectedIndexRef.current = items.length - 1;
    }
  }, [items, selectedIds.size]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedIndexRef.current = null;
  }, []);

  const setSelected = useCallback(
    (ids: readonly string[], selected: boolean) => {
      const known = new Set(items.map((i) => i.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) {
          if (!known.has(id)) continue;
          if (selected) next.add(id);
          else next.delete(id);
        }
        return next;
      });
    },
    [items],
  );

  const isAllSelected = items.length > 0 && selectedIds.size === items.length;
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  return {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    setSelected,
    isAllSelected,
    isSomeSelected,
    selectedCount: selectedIds.size,
  };
}
